import { useCallback, useEffect, useState } from 'react';
import { hubService } from '../../services/hub.service';
import { errorText } from '../../lib/assistantView';
import { fmtTime } from '../../lib/jobView';
import { formatBytes } from '../../lib/sourceView';

interface Snapshot {
  id: string;
  snapshotNo: number;
  status: string;
  createdAt: string;
  sourceCount: number;
}

interface Detail {
  entries: { url: string; version: number; bytes: number | null }[];
}

/**
 * Knowledge › Snapshots (A2 step C1, ported from chatbot_front hub/SnapshotsPanel.js): each build saves the collected
 * pages as a numbered snapshot; exactly one is active. Re-index rebuilds the bot's chunks from a snapshot — the active
 * one (after a chunking-prompt change) or an older one (to go back). The job runs under Builder › Jobs; if it would
 * leave fewer chunks than now, it waits for approval there.
 */
export function SnapshotsPanel({ system, botId, onJobStarted }: { system: string; botId: string; onJobStarted: () => void }) {
  const [rows, setRows] = useState<Snapshot[] | null>(null);
  const [open, setOpen] = useState<Record<string, Detail | 'loading'>>({});
  const [force, setForce] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      const r = await hubService.knowledge<Snapshot[]>(system, botId, 'GET', 'snapshots');
      setRows(Array.isArray(r) ? r : []);
    } catch (e) {
      setRows([]);
      setError(errorText(e));
    }
  }, [system, botId]);

  useEffect(() => {
    void load();
  }, [load]);

  const close = (id: string) =>
    setOpen((o) => {
      const next = { ...o };
      delete next[id];
      return next;
    });

  async function toggle(s: Snapshot) {
    if (open[s.id]) {
      close(s.id);
      return;
    }
    setOpen((o) => ({ ...o, [s.id]: 'loading' }));
    try {
      const d = await hubService.knowledge<Detail>(system, botId, 'GET', `snapshots/${encodeURIComponent(s.id)}`);
      setOpen((o) => ({ ...o, [s.id]: d }));
    } catch (e) {
      close(s.id);
      setError(errorText(e));
    }
  }

  async function reindex(s: Snapshot) {
    const active = s.status === 'active';
    const msg = active
      ? `Rebuild the bot’s chunks from snapshot #${s.snapshotNo} (the active one)? This runs the chunking LLM over ${s.sourceCount} page(s).`
      : `Make snapshot #${s.snapshotNo} the active one and rebuild the bot’s chunks from its ${s.sourceCount} page(s)? The bot answers from it once the job finishes.`;
    if (!window.confirm(msg)) return;
    setBusy(s.id);
    setError(null);
    try {
      await hubService.knowledge(system, botId, 'POST', `snapshots/${encodeURIComponent(s.id)}/reindex`, { force });
      setNotice(`Re-index of snapshot #${s.snapshotNo} started.`);
      onJobStarted();
      await load();
    } catch (e) {
      setError(errorText(e));
    } finally {
      setBusy(null);
    }
  }

  if (rows === null) return <p className="text-sm text-gray-500">Loading…</p>;

  return (
    <section className="bg-white rounded-lg border border-gray-200 p-4 space-y-4">
      <div>
        <h2 className="font-medium text-gray-900">Snapshots</h2>
        <p className="text-sm text-gray-600">Every build saves the pages it collected as a snapshot. One is active; re-index rebuilds the bot’s chunks from a snapshot.</p>
      </div>
      {error && <div className="text-sm text-red-600 bg-red-50 border border-red-100 rounded px-3 py-2">{error}</div>}
      {notice && <div className="text-sm text-amber-800 bg-amber-50 border border-amber-100 rounded px-3 py-2">{notice}</div>}
      <label className="text-sm flex items-center gap-2">
        <input type="checkbox" checked={force} onChange={(e) => setForce(e.target.checked)} />
        Rebuild even if nothing changed (e.g. after a chunking-prompt change)
      </label>
      {rows.length === 0 ? (
        <p className="text-sm text-gray-500">No snapshots yet — run a build on the Builder tab.</p>
      ) : (
        <ul className="space-y-2">
          {rows.map((s) => {
            const d = open[s.id];
            return (
              <li key={s.id} className="border rounded p-2 text-sm">
                <div className="flex flex-wrap items-center gap-3">
                  <button type="button" className="text-gray-500" onClick={() => void toggle(s)}>
                    {d ? '▾' : '▸'}
                  </button>
                  <span className="font-medium">#{s.snapshotNo}</span>
                  {s.status === 'active' ? (
                    <span className="text-xs px-1.5 py-0.5 rounded bg-green-50 text-green-700">active</span>
                  ) : (
                    <span className="text-xs text-gray-500">{s.status}</span>
                  )}
                  <span className="text-gray-500 text-xs">{fmtTime(s.createdAt)}</span>
                  <span className="text-gray-600">{s.sourceCount} page(s)</span>
                  <button type="button" className="ml-auto text-blue-600 hover:underline" disabled={busy === s.id} onClick={() => void reindex(s)}>
                    {s.status === 'active' ? 'Re-index' : 'Make active and re-index'}
                  </button>
                </div>
                {d === 'loading' && <p className="text-xs text-gray-500 mt-1">Loading…</p>}
                {d && d !== 'loading' && (
                  <ul className="mt-2 text-xs max-h-60 overflow-auto divide-y">
                    {d.entries.map((e) => (
                      <li key={e.url} className="flex justify-between gap-2 py-0.5">
                        <span className="truncate" title={e.url}>
                          {e.url}
                        </span>
                        <span className="text-gray-500 shrink-0">
                          v{e.version} · {formatBytes(e.bytes)}
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}
